import React, { useState, useEffect, useCallback } from 'react';
import { FolderKanban, Plus, Edit3, Trash2, X, Save, Train, Bus, ChevronDown, ChevronUp, BarChart3, Armchair, Package, CalendarDays, Check, Clock, List, EyeOff, Eye, Upload, FileSpreadsheet, AlertTriangle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MdpCockpit from './MdpCockpit';
import HitachiConfigPanel from '../components/planning/HitachiConfigPanel';
import { MdpImportModal } from './reports/MdpScheduleGrid';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Train', 'Coach', 'City Bus'];

const CATEGORY_CONFIG = {
  'Train': { unit: 'seat', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Train },
  'Coach': { unit: 'set', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Bus },
  'City Bus': { unit: 'set', color: 'bg-green-100 text-green-700 border-green-200', icon: Bus },
};

/* ============================================================
   PLAN ENTRIES DETAIL - Shows all plan entries for a project
   ============================================================ */
function PlanEntriesDetail({ projectId, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [totals, setTotals] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/plan-entries`);
      setEntries(res.data.entries || []);
      setTotals(res.data.totals || {});
    } catch { setEntries([]); }
  }, [projectId]);

  useEffect(() => { if (open && !entries) fetchEntries(); }, [open, entries, fetchEntries]);

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this plan entry?')) return;
    try {
      await axios.delete(`${API}/api/plans/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-600"
        data-testid={`plan-entries-toggle-${projectId}`}>
        <List className="w-3 h-3" />
        Plan Entries
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && entries && (
        <div className="mt-1.5 border border-slate-200 rounded-lg overflow-hidden bg-white">
          {totals && (
            <div className="px-3 py-1.5 bg-slate-50 border-b text-[10px] font-bold text-slate-500 flex gap-4">
              <span>Total: <span className="text-blue-600">{totals.planned}P</span> / <span className="text-emerald-600">{totals.completed}C</span> / <span className="text-violet-600">{totals.shipped}S</span></span>
              <span className="text-slate-400">{entries.length} entries</span>
            </div>
          )}
          <table className="w-full text-[10px]">
            <thead className="bg-slate-50 text-[9px] text-slate-400 uppercase">
              <tr>
                <th className="px-2 py-1 text-left font-semibold">Date</th>
                <th className="px-2 py-1 text-left font-semibold">Set#</th>
                <th className="px-2 py-1 text-center font-semibold">Planned</th>
                <th className="px-2 py-1 text-center font-semibold">Done</th>
                <th className="px-2 py-1 text-center font-semibold">Shipped</th>
                <th className="px-2 py-1 text-left font-semibold">Status</th>
                {isAdmin && <th className="px-2 py-1"></th>}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-blue-50/20">
                  <td className="px-2 py-1.5 font-medium text-slate-600">{e.date}</td>
                  <td className="px-2 py-1.5">{e.set_numbers ? <span className="text-indigo-600 font-bold">{e.set_numbers}</span> : e.set_number ? <span className="text-indigo-600 font-bold">Set #{e.set_number}</span> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-2 py-1.5 text-center font-bold text-blue-600">{e.planned}</td>
                  <td className="px-2 py-1.5 text-center font-bold text-emerald-600">{e.completed}</td>
                  <td className="px-2 py-1.5 text-center font-bold text-violet-600">{e.shipped}</td>
                  <td className="px-2 py-1.5"><span className={`px-1 py-0.5 rounded text-[8px] font-semibold ${e.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : e.status === 'rescheduled' ? 'bg-purple-100 text-purple-600' : 'bg-blue-50 text-blue-500'}`}>{e.status}</span></td>
                  {isAdmin && (
                    <td className="px-2 py-1.5">
                      <button onClick={() => handleDeleteEntry(e.id)} className="p-0.5 hover:bg-red-50 rounded text-slate-300 hover:text-red-500" title="Delete this entry">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="px-3 py-4 text-center text-slate-400">No plan entries</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   CRR VARIANTS BADGES - shown at the top of a City Bus project card
   ============================================================ */
function CrrVariantsBadges({ projectId }) {
  const [variants, setVariants] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/planning/projects/${projectId}/crrs`);
        if (!cancel) setVariants(res.data.crrs || []);
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [projectId]);

  if (!variants.length) return null;
  return (
    <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid={`crr-variants-badges-${projectId}`}>
      <span className="text-[9px] font-bold text-indigo-500 uppercase tracking-wider">CRRs</span>
      {variants.map(v => {
        const code = v.is_primary ? (v.bill_number || 'Primary') : v.nfi_code;
        const cls = v.is_primary
          ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
          : 'text-indigo-700 bg-indigo-50 border-indigo-200';
        const codeCls = v.is_primary ? 'text-emerald-500' : 'text-indigo-500';
        return (
          <span key={v.is_primary ? '__primary__' : v.nfi_code}
            className={`text-[10px] font-semibold border px-2 py-0.5 rounded ${cls}`}
            title={`${v.is_primary ? 'Primary CRR' : 'Variant'} · ${v.total_items || 0} items`}>
            {v.label && <span className="mr-1">{v.label}</span>}
            <span className={`font-mono ${codeCls}`}>{code}</span>
            <span className={`text-[9px] ml-1 ${codeCls}`}>({v.total_items || 0})</span>
          </span>
        );
      })}
    </div>
  );
}


/* ============================================================
   SET SCHEDULE PANEL - Dock Date & Shipment dates per set
   ============================================================ */
function SetSchedulePanel({ projectId, orderedQty, seatTypeName, isAdmin = true, salesOrders = [] }) {
  const [schedules, setSchedules] = useState([]);
  const [derivedStatus, setDerivedStatus] = useState([]);
  const [crrVariants, setCrrVariants] = useState([]); // [{nfi_code, label, is_primary}]
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/set-schedules`);
      const existing = res.data.schedules || [];
      const rows = [];
      for (let i = 1; i <= orderedQty; i++) {
        const found = existing.find(s => s.set_number === i);
        rows.push(found || { set_number: i, line_entry_date: '', shipment_date: '', status: 'pending', actual_ship_date: '', source: '', comment: '', nfi_code: '' });
      }
      setSchedules(rows);
    } catch { /* ignore */ }
  }, [projectId, orderedQty]);

  const fetchCrrVariants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/crrs`);
      setCrrVariants(res.data.crrs || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const fetchDerived = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/set-derived-status`);
      setDerivedStatus(res.data.schedules || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { fetchSchedules(); fetchDerived(); fetchCrrVariants(); }, [fetchSchedules, fetchDerived, fetchCrrVariants]);

  const handleSetNumberChange = (oldNum, newNum) => {
    const parsed = parseInt(newNum);
    if (isNaN(parsed) || parsed < 1) return;
    setSchedules(prev => prev.map(s => s.set_number === oldNum ? { ...s, set_number: parsed } : s));
    setDirty(true);
  };

  const handleDateChange = (setNum, value) => {
    setSchedules(prev => prev.map(s => {
      if (s.set_number !== setNum) return s;
      let shipDate = '';
      if (value) {
        const d = new Date(value);
        d.setDate(d.getDate() - 5);
        shipDate = d.toISOString().split('T')[0];
      }
      return { ...s, line_entry_date: value, shipment_date: shipDate };
    }));
    setDirty(true);
  };

  const handleNfiChange = (setNum, value) => {
    setSchedules(prev => prev.map(s => s.set_number === setNum ? { ...s, nfi_code: value } : s));
    setDirty(true);
  };

  const handleCommentChange = (setNum, value) => {
    setSchedules(prev => prev.map(s => s.set_number === setNum ? { ...s, comment: value } : s));
    setDirty(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await axios.put(`${API}/api/planning/projects/${projectId}/set-schedules`, {
        schedules: schedules.filter(s => s.line_entry_date || s.source || s.comment || s.nfi_code),
      });
      setSchedules(prev => {
        const saved = res.data.schedules || [];
        return prev.map(s => {
          const found = saved.find(x => x.set_number === s.set_number);
          return found || s;
        });
      });
      setDirty(false);
      toast.success('Set schedules saved');
    } catch {
      toast.error('Failed to save schedules');
    }
    setLoading(false);
  };

  if (orderedQty === 0) return null;

  return (
    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden" data-testid="set-schedule-panel">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200 hover:bg-slate-100 transition-colors"
        data-testid="set-schedule-toggle">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-[11px] font-semibold text-slate-700">Set Schedule</span>
          <span className="text-[10px] text-slate-400">({orderedQty} sets)</span>
        </div>
        <div className="flex items-center gap-2">
          {dirty && isAdmin && (
            <button onClick={(e) => { e.stopPropagation(); handleSave(); }} disabled={loading}
              className="flex items-center gap-1 text-[10px] font-semibold text-white bg-orange-500 hover:bg-orange-600 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
              data-testid="save-set-schedules-btn">
              <Save className="w-3 h-3" />{loading ? 'Saving...' : 'Save'}
            </button>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </button>
      {expanded && (
      <>
      {/* NFI breakdown row — quick summary of how sets are distributed across CRR variants */}
      {(crrVariants.length > 0 || schedules.some(s => s.nfi_code)) && (
        <div className="px-3 py-2 bg-indigo-50/40 border-b border-indigo-100 flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-bold text-indigo-500 uppercase tracking-wider">NFI Breakdown</span>
          {(() => {
            const counts = {};
            schedules.forEach(s => {
              const key = s.nfi_code || '';
              counts[key] = (counts[key] || 0) + 1;
            });
            const chips = [];
            // Known variants first
            crrVariants.forEach(v => {
              const key = v.is_primary ? '__primary__' : v.nfi_code;
              const n = counts[key] || 0;
              const disp = v.is_primary
                ? `Primary${v.bill_number ? ' · ' + v.bill_number : ''}`
                : `${v.nfi_code}${v.label ? ' · ' + v.label : ''}`;
              chips.push(
                <span key={key} className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${n > 0 ? 'bg-white border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                  {disp}: <span className="font-black">{n}</span>
                </span>
              );
            });
            // Unknown NFI codes on sets (e.g. deleted variant)
            Object.entries(counts).forEach(([k, n]) => {
              if (!k) return;
              const known = crrVariants.some(v => (v.is_primary ? '__primary__' : v.nfi_code) === k);
              if (!known) {
                chips.push(
                  <span key={k} className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-amber-50 border-amber-300 text-amber-700">
                    {k}: <span className="font-black">{n}</span> <span className="text-[8px] italic">(missing CRR)</span>
                  </span>
                );
              }
            });
            const unassigned = counts[''] || 0;
            if (unassigned > 0) {
              chips.push(
                <span key="unassigned" className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-slate-100 border-slate-300 text-slate-500">
                  Unassigned: <span className="font-black">{unassigned}</span>
                </span>
              );
            }
            return chips;
          })()}
        </div>
      )}
      <table className="w-full text-[11px]">
        <thead>
          <tr className="bg-slate-50/50 text-[9px] text-slate-400 uppercase tracking-wider">
            <th className="px-3 py-1.5 text-left font-semibold w-14">Set #</th>
            <th className="px-3 py-1.5 text-left font-semibold">SO</th>
            <th className="px-3 py-1.5 text-left font-semibold">Dock Date</th>
            <th className="px-3 py-1.5 text-left font-semibold">Ship Date <span className="normal-case text-[8px] text-slate-300">(-5d)</span></th>
            <th className="px-3 py-1.5 text-left font-semibold">Variant / NFI</th>
            <th className="px-3 py-1.5 text-left font-semibold">Comment</th>
            <th className="px-3 py-1.5 text-center font-semibold">Planned</th>
            <th className="px-3 py-1.5 text-center font-semibold">Produced</th>
            <th className="px-3 py-1.5 text-center font-semibold">Shipped</th>
            <th className="px-3 py-1.5 text-center font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((s) => {
            const derived = derivedStatus.find(d => d.set_number === s.set_number) || {};
            const statusMap = {
              shipped: { label: derived.days_late > 0 ? `Shipped (${derived.days_late}d late)` : 'Shipped', cls: derived.days_late > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-600' },
              produced: { label: 'Produced', cls: 'bg-blue-100 text-blue-600' },
              planned: { label: 'Planned', cls: 'bg-blue-50 text-blue-600' },
              pending: { label: s.line_entry_date ? 'Scheduled' : 'No date', cls: s.line_entry_date ? 'bg-slate-100 text-slate-500' : 'bg-slate-50 text-slate-300' },
            };
            const st = statusMap[derived.status] || statusMap.pending;
            return (
              <tr key={s.set_number} className={`border-t border-slate-100 hover:bg-orange-50/30 ${derived.status === 'shipped' ? 'bg-emerald-50/20' : ''}`}>
                <td className="px-3 py-2">
                  {isAdmin ? (
                    <input type="number" value={s.set_number} min={1}
                      onChange={e => handleSetNumberChange(s.set_number, e.target.value)}
                      className="w-14 border border-slate-200 rounded px-2 py-1 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-orange-400"
                      data-testid={`set-number-${s.set_number}`} />
                  ) : (
                    <span className="font-bold text-slate-700">Set {s.set_number}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {(() => {
                    const matching = (salesOrders || []).filter(so =>
                      // Project-level SO (no set_numbers) applies to all sets
                      !so.set_numbers || so.set_numbers.length === 0
                        ? true
                        : so.set_numbers.includes(s.set_number)
                    );
                    if (matching.length === 0) return <span className="text-slate-300">-</span>;
                    return (
                      <div className="flex flex-wrap gap-1">
                        {matching.map((so, idx) => (
                          <span key={idx} className="text-[9px] font-mono font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded whitespace-nowrap"
                            data-testid={`set-so-${s.set_number}-${idx}`}>
                            {so.so_number}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2">
                  <input type="date" value={s.line_entry_date || ''} onChange={(e) => handleDateChange(s.set_number, e.target.value)}
                    disabled={!isAdmin}
                    className={`border border-slate-200 rounded px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-orange-400 ${!isAdmin ? 'bg-slate-50 cursor-default' : ''}`}
                    data-testid={`line-entry-date-${s.set_number}`} />
                </td>
                <td className="px-3 py-2">
                  {s.shipment_date ? (
                    <span className="font-medium text-slate-700">{s.shipment_date}</span>
                  ) : <span className="text-slate-300">-</span>}
                </td>
                <td className="px-3 py-2">
                  <select value={s.nfi_code || ''} onChange={(e) => handleNfiChange(s.set_number, e.target.value)}
                    disabled={!isAdmin}
                    className={`border border-slate-200 rounded px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[150px] max-w-[220px] ${!isAdmin ? 'bg-slate-50 cursor-default' : ''} ${s.nfi_code ? 'text-indigo-700 font-semibold' : 'text-slate-400'}`}
                    data-testid={`set-nfi-${s.set_number}`}>
                    <option value="">— Not assigned —</option>
                    {crrVariants.map(v => {
                      const val = v.is_primary ? '__primary__' : v.nfi_code;
                      const displayLabel = v.label || (v.is_primary ? 'Primary CRR' : v.nfi_code);
                      const codeSuffix = v.is_primary
                        ? (v.bill_number ? ` (${v.bill_number})` : '')
                        : ` (${v.nfi_code})`;
                      return <option key={val} value={val}>{displayLabel}{codeSuffix}</option>;
                    })}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={s.comment || ''} onChange={(e) => handleCommentChange(s.set_number, e.target.value)}
                    disabled={!isAdmin}
                    placeholder={isAdmin ? 'Note…' : ''}
                    className={`border border-slate-200 rounded px-2 py-1 text-[10px] w-40 focus:outline-none focus:ring-1 focus:ring-orange-400 ${!isAdmin ? 'bg-slate-50 cursor-default' : ''}`}
                    data-testid={`set-comment-${s.set_number}`} />
                </td>
                <td className="px-3 py-2 text-center">
                  {derived.planned_date ? <span className="text-[10px] text-blue-600 font-medium">{derived.planned_date}</span> : <span className="text-slate-300">-</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {derived.produced_date ? <span className="text-[10px] text-emerald-600 font-medium">{derived.produced_date}</span> : <span className="text-slate-300">-</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {derived.shipped_date ? <span className="text-[10px] text-violet-600 font-medium">{derived.shipped_date}</span> : <span className="text-slate-300">-</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`} data-testid={`set-status-${s.set_number}`}>{st.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </>
      )}
    </div>
  );
}

/* ============================================================
   CITY BUS SET TRACKER - Per-set, per-component (Std/Flip-Up/Barrier)
   ============================================================ */
const COMP_KEYS = ['std_seat', 'flip_up', 'barrier'];
const COMP_LABELS = { std_seat: 'Std Seat', flip_up: 'Flip-Up', barrier: 'Barrier' };
const FIELD_LABELS = { planned: 'P', done: 'D', shipped: 'S' };
const FIELD_COLORS = {
  planned: { on: 'bg-blue-500 text-white', off: 'bg-blue-50 text-blue-300 border border-blue-200' },
  done: { on: 'bg-emerald-500 text-white', off: 'bg-emerald-50 text-emerald-300 border border-emerald-200' },
  shipped: { on: 'bg-violet-500 text-white', off: 'bg-violet-50 text-violet-300 border border-violet-200' },
};


// CRR Panel - Upload and view Component Requirement Report
function CRRPanel({ projectId, isAdmin }) {
  const [crr, setCrr] = useState(null);
  const [crrList, setCrrList] = useState([]);        // [{nfi_code, label, bill_number, total_items, is_primary}]
  const [currentNfi, setCurrentNfi] = useState('');  // '' = primary
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ nfi_code: '', label: '' });
  const fileRef = React.useRef(null);
  const uploadFileRef = React.useRef(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/crrs`);
      setCrrList(res.data.crrs || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const fetchCrr = useCallback(async (nfi = '') => {
    try {
      const res = await axios.get(`${API}/api/planning/projects/${projectId}/crr`, {
        params: { nfi_code: nfi },
      });
      setCrr(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchList(); fetchCrr(''); }, [fetchList, fetchCrr]);

  const switchTab = (nfi) => {
    setCurrentNfi(nfi);
    setLoading(true);
    fetchCrr(nfi);
  };

  const handleUpload = async (e) => {
    // Legacy quick-upload: replaces PRIMARY CRR (no nfi_code prompt)
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/planning/projects/${projectId}/crr/upload`, fd);
      toast.success(`Primary CRR uploaded: ${res.data.total_items} items (${res.data.items_short} short)`);
      if (res.data.parse_warning) toast.warning(res.data.parse_warning, { duration: 12000 });
      await fetchList();
      setCurrentNfi('');
      fetchCrr('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleVariantUpload = async () => {
    const file = uploadFileRef.current?.files?.[0];
    if (!file) { toast.error('Please choose a file'); return; }
    if (!uploadForm.nfi_code.trim()) { toast.error('NFI code is required for a variant CRR'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('nfi_code', uploadForm.nfi_code.trim());
      fd.append('label', uploadForm.label.trim());
      const res = await axios.post(`${API}/api/planning/projects/${projectId}/crr/upload`, fd);
      toast.success(`CRR variant "${res.data.nfi_code}" saved: ${res.data.total_items} items`);
      if (res.data.parse_warning) toast.warning(res.data.parse_warning, { duration: 12000 });
      setShowUpload(false);
      setUploadForm({ nfi_code: '', label: '' });
      await fetchList();
      switchTab(res.data.nfi_code);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDeleteVariant = async (nfi) => {
    const label = nfi ? `variant "${nfi}"` : 'PRIMARY CRR';
    const warning = nfi
      ? `Delete CRR variant "${nfi}"?`
      : `⚠ Delete the PRIMARY CRR?\n\nThis is the fallback source for any set that isn't assigned to a variant, and it feeds material planning. Only delete if you plan to re-upload immediately.`;
    if (!window.confirm(warning)) return;
    try {
      const target = nfi || '__primary__';
      await axios.delete(`${API}/api/planning/projects/${projectId}/crr/${encodeURIComponent(target)}`);
      toast.success(`${label} deleted`);
      await fetchList();
      switchTab('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleReplaceActive = () => {
    // If active tab is primary → use quick file input (existing "Update Primary" button)
    // If active tab is variant → open the variant modal pre-filled with current NFI/label
    if (currentNfi === '') {
      fileRef.current?.click();
    } else {
      const c = crrList.find(x => (x.nfi_code || '') === currentNfi);
      setUploadForm({ nfi_code: currentNfi, label: c?.label || '' });
      setShowUpload(true);
    }
  };

  const handleRenameLabel = async (crrItem) => {
    const nfi = crrItem.is_primary ? '__primary__' : crrItem.nfi_code;
    const current = crrItem.label || '';
    const next = window.prompt(
      `Set a display label for this CRR${crrItem.is_primary ? ' (Primary)' : ` (${crrItem.nfi_code})`}\n\nThis label appears in the Set Schedule "Variant" dropdown.`,
      current,
    );
    if (next === null) return;
    try {
      await axios.patch(`${API}/api/planning/projects/${projectId}/crr/${encodeURIComponent(nfi)}/label`,
        { label: next });
      toast.success('Label updated');
      await fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
  };

  const hasData = crr && crr.total_items > 0;
  const s = crr?.summary || {};
  const items = crr?.items || [];
  const filtered = filter === 'all' ? items
    : filter === 'short' ? items.filter(i => i.qty_short > 0)
    : filter === 'available' ? items.filter(i => i.qty_available >= i.qty_required && i.qty_required > 0)
    : filter === 'no-stock' ? items.filter(i => i.qty_available === 0 && i.qty_required > 0)
    : items;
  const hasVariants = crrList.length > 1 || crrList.some(c => !c.is_primary);

  return (
    <div className="mt-2 bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`crr-panel-${projectId}`}>
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => hasData && setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-cyan-500" />
          <span className="text-[11px] font-semibold text-slate-700">Component Requirement Report (CRR)</span>
          {hasData && (
            <span className="text-[10px] text-slate-400">({crr.total_items} items)</span>
          )}
          {hasData && s.short > 0 && (
            <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200">
              {s.short} short
            </span>
          )}
          {hasData && s.short === 0 && (
            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
              All available
            </span>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {isAdmin && (
            <>
              <label className="cursor-pointer flex items-center gap-1 px-2 py-1 bg-cyan-600 text-white rounded text-[10px] font-semibold hover:bg-cyan-700 transition-colors"
                data-testid={`crr-upload-${projectId}`}>
                <Upload className="w-3 h-3" />
                {uploading && currentNfi === '' ? 'Uploading...' : (crrList.some(c=>c.is_primary) ? 'Update Primary' : 'Upload Primary')}
                <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
              <button onClick={() => setShowUpload(true)}
                className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded text-[10px] font-semibold hover:bg-indigo-700 transition-colors"
                data-testid={`crr-add-variant-${projectId}`}>
                <Plus className="w-3 h-3" /> Variant CRR
              </button>
            </>
          )}
          {crrList.length > 0 && (expanded ? <ChevronUp className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => setExpanded(!expanded)} /> : <ChevronDown className="w-4 h-4 text-slate-400 cursor-pointer" onClick={() => setExpanded(!expanded)} />)}
        </div>
      </div>

      {/* Variant tabs */}
      {expanded && hasVariants && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-3 pt-2 pb-0 flex items-end gap-1 flex-wrap">
          {crrList.map((c) => {
            const key = c.nfi_code || 'primary';
            const active = (c.nfi_code || '') === currentNfi;
            return (
              <button key={key} onClick={() => switchTab(c.nfi_code || '')}
                className={`text-[10px] px-3 py-1.5 rounded-t-md border border-b-0 flex items-center gap-1.5 transition-colors ${active ? 'bg-white border-slate-300 text-cyan-700 font-bold shadow-sm' : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-white'}`}
                data-testid={`crr-tab-${key}`}>
                {c.is_primary ? (
                  <span className="text-[8px] font-bold bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">PRIMARY</span>
                ) : (
                  <span className="text-[8px] font-bold bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded">NFI</span>
                )}
                <span>{c.label || (c.is_primary ? 'Primary CRR' : c.nfi_code)}</span>
                {!c.is_primary && (
                  <span className="text-slate-400 text-[9px]">· {c.nfi_code}</span>
                )}
                {active && isAdmin && (
                  <>
                    <Edit3 className="w-3 h-3 text-slate-400 hover:text-cyan-600 ml-0.5"
                      title="Rename label"
                      onClick={(e) => { e.stopPropagation(); handleRenameLabel(c); }} />
                    <Upload className="w-3 h-3 text-slate-400 hover:text-emerald-600"
                      title={c.is_primary ? 'Replace primary CRR file' : 'Replace this variant file'}
                      onClick={(e) => { e.stopPropagation(); handleReplaceActive(); }} />
                    <Trash2 className="w-3 h-3 text-slate-400 hover:text-red-500"
                      title={c.is_primary ? 'Delete primary CRR (warning!)' : `Delete variant ${c.nfi_code}`}
                      onClick={(e) => { e.stopPropagation(); handleDeleteVariant(c.is_primary ? '' : c.nfi_code); }} />
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {expanded && hasData && (
        <div className="border-t border-slate-100 px-3 py-3">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            {[
              { key: 'all', label: 'Total', value: s.total, color: 'slate' },
              { key: 'available', label: 'Available', value: s.available, color: 'emerald' },
              { key: 'short', label: 'Short', value: s.short, color: 'red' },
              { key: 'no-stock', label: 'No Stock', value: s.no_stock, color: 'orange' },
            ].map(c => (
              <button key={c.key} onClick={() => setFilter(c.key)}
                className={`text-center p-2 rounded-lg border transition-all text-[10px] ${filter === c.key ? `bg-${c.color}-50 border-${c.color}-300 ring-1 ring-${c.color}-200` : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                <div className={`text-lg font-black text-${c.color}-600`}>{c.value || 0}</div>
                <div className={`text-${c.color}-500 font-semibold uppercase`}>{c.label}</div>
              </button>
            ))}
          </div>

          {/* Group summary */}
          {crr.group_summary && Object.keys(crr.group_summary).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Object.entries(crr.group_summary).sort((a,b) => b[1].short - a[1].short).map(([g, gs]) => (
                <span key={g} className={`text-[9px] px-2 py-1 rounded-md border ${gs.short > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                  <strong>{g}</strong>: {gs.total} items {gs.short > 0 && <span className="font-bold">({gs.short} short)</span>}
                </span>
              ))}
            </div>
          )}

          {/* Items table */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-slate-400 uppercase tracking-wider text-[8px]">
                  <th className="px-2 py-1.5 text-left font-semibold">Item Code</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Group</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Req</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-indigo-500">KSU Stock</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-orange-500">Kiel Stock</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-slate-600">Total</th>
                  <th className="px-2 py-1.5 text-right font-semibold text-red-400">Short</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => {
                  const isShort = (item.total_short || 0) > 0;
                  const noStock = (item.total_stock || 0) === 0 && item.qty_required > 0;
                  return (
                    <tr key={item.item_code} className={`${isShort ? 'bg-red-50/50' : noStock ? 'bg-orange-50/50' : i % 2 ? 'bg-slate-50/30' : ''} hover:bg-blue-50/30`}>
                      <td className="px-2 py-1 font-mono font-semibold text-slate-700">{item.item_code}</td>
                      <td className="px-2 py-1 text-slate-600 max-w-[180px] truncate" title={item.description}>{item.description}</td>
                      <td className="px-2 py-1 text-slate-500">{item.group || '-'}</td>
                      <td className="px-2 py-1 text-right text-blue-600 font-semibold">{item.qty_required}</td>
                      <td className="px-2 py-1 text-right text-indigo-600">{item.ksu_available || 0}</td>
                      <td className="px-2 py-1 text-right text-orange-600">{item.kiel_available || 0}</td>
                      <td className={`px-2 py-1 text-right font-bold ${noStock ? 'text-orange-600' : 'text-slate-700'}`}>{item.total_stock || 0}</td>
                      <td className={`px-2 py-1 text-right font-bold ${isShort ? 'text-red-600' : 'text-slate-300'}`}>{item.total_short || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] text-slate-400 mt-2">
            Uploaded: {crr.uploaded_at ? new Date(crr.uploaded_at).toLocaleString() : '-'} | File: {crr.filename || '-'}
            {crr.nfi_code && <span> | <strong className="text-indigo-600">NFI:</strong> {crr.nfi_code}</span>}
            {!crr.is_primary && <span className="ml-2 text-indigo-500 italic">(Variant — used by sets assigned to this NFI in the Set Schedule)</span>}
          </div>
        </div>
      )}

      {/* Variant Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="crr-variant-upload-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-700 to-cyan-700 flex items-center justify-between">
              <div className="text-lg font-bold text-white flex items-center gap-2">
                <Upload className="w-5 h-5" /> Upload Variant CRR
              </div>
              <button onClick={() => { setShowUpload(false); setUploadForm({ nfi_code: '', label: '' }); }}
                className="text-indigo-100 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                Each CRR variant is tied to an <strong>NFI code</strong> and shown as its own tab.
                Sets you assign to this variant in the Set Schedule table will pull demand
                from <span className="font-semibold text-indigo-600">this CRR</span> instead of the primary during material planning.
              </p>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase">NFI Code *</label>
                <input type="text" value={uploadForm.nfi_code}
                  onChange={e => setUploadForm(f => ({ ...f, nfi_code: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  placeholder="e.g. NFI-3068-CBU"
                  data-testid="variant-nfi-input" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase">Label (optional)</label>
                <input type="text" value={uploadForm.label}
                  onChange={e => setUploadForm(f => ({ ...f, label: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  placeholder="e.g. Kiel PL (Full Set)"
                  data-testid="variant-label-input" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase">CRR File *</label>
                <input ref={uploadFileRef} type="file" accept=".xls,.xlsx"
                  className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer mt-1"
                  data-testid="variant-file-input" />
              </div>
            </div>
            <div className="px-6 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-200">
              <button onClick={() => { setShowUpload(false); setUploadForm({ nfi_code: '', label: '' }); }}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={handleVariantUpload} disabled={uploading}
                className="px-5 py-2 bg-indigo-700 text-white rounded-lg text-sm font-semibold hover:bg-indigo-800 disabled:opacity-60 flex items-center gap-2"
                data-testid="variant-upload-submit">
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CityBusSetTracker({ projectId, orderedQty, readOnly }) {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/plans/citybus-component-summary/${projectId}`);
      setSets(res.data.sets || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleField = async (setNum, comp, field, currentVal) => {
    const newVal = !currentVal;
    const date = newVal ? new Date().toISOString().split('T')[0] : '';
    try {
      await axios.put(`${API}/api/planning/projects/${projectId}/set-component-tracking`, {
        set_number: setNum, component: comp, field, value: newVal, date
      });
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  if (orderedQty === 0) return null;
  if (loading) return <div className="mt-3 p-3 text-xs text-slate-400">Loading tracker...</div>;

  const totalComplete = sets.filter(s => s.status === 'complete').length;
  const totalPartial = sets.filter(s => s.status === 'partial').length;

  return (
    <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden" data-testid="citybus-set-tracker">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full bg-green-50 px-3 py-2 flex items-center justify-between border-b border-green-200 hover:bg-green-100 transition-colors"
        data-testid="citybus-tracker-toggle">
        <div className="flex items-center gap-2">
          <Bus className="w-3.5 h-3.5 text-green-600" />
          <span className="text-[11px] font-semibold text-slate-700">Set Component Tracker {readOnly ? '(read-only)' : ''}</span>
          <span className="text-[10px] text-slate-400">({orderedQty} sets)</span>
        </div>
        <div className="flex items-center gap-2">
          {totalComplete > 0 && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{totalComplete} complete</span>}
          {totalPartial > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{totalPartial} partial</span>}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </button>
      {expanded && (
      <>
      <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table className="w-full text-[10px]">
          <thead>
            <tr className="bg-slate-50/50 text-[8px] text-slate-400 uppercase tracking-wider">
              <th className="px-2 py-1.5 text-left font-semibold w-14">Set</th>
              {COMP_KEYS.map(ck => (
                <th key={ck} className="px-2 py-1.5 text-center font-semibold" colSpan={3}>
                  <span>{COMP_LABELS[ck]}</span>
                  <div className="flex justify-center gap-1 mt-0.5 text-[7px] font-medium text-slate-300">
                    <span className="w-5">P</span><span className="w-5">D</span><span className="w-5">S</span>
                  </div>
                </th>
              ))}
              <th className="px-2 py-1.5 text-center font-semibold w-16">Status</th>
            </tr>
          </thead>
          <tbody>
            {sets.map(s => (
              <tr key={s.set_number} className={`border-t border-slate-100 hover:bg-green-50/30 ${s.status === 'complete' ? 'bg-emerald-50/20' : ''}`}>
                <td className="px-2 py-1.5"><span className="font-bold text-slate-700">#{s.set_number}</span></td>
                {COMP_KEYS.map(ck => {
                  const comp = s.components[ck] || {};
                  return ['planned', 'done', 'shipped'].map(f => (
                    <td key={`${ck}-${f}`} className="px-0.5 py-1.5 text-center">
                      {readOnly ? (
                        <span className={`inline-flex w-5 h-5 rounded text-[8px] font-bold items-center justify-center ${comp[f] ? FIELD_COLORS[f].on : 'bg-slate-50 text-slate-200 border border-slate-100'}`}
                          title={`${COMP_LABELS[ck]} - ${f}${comp[f + '_date'] || comp[f === 'done' ? 'done_date' : f === 'planned' ? 'plan_date' : 'ship_date'] ? ' (' + (comp[f === 'done' ? 'done_date' : f === 'planned' ? 'plan_date' : 'ship_date']) + ')' : ''}`}>
                          {comp[f] ? <Check className="w-3 h-3" /> : <span className="text-[7px]">{FIELD_LABELS[f]}</span>}
                        </span>
                      ) : (
                      <button
                        onClick={() => toggleField(s.set_number, ck, f, comp[f])}
                        className={`w-5 h-5 rounded text-[8px] font-bold transition-all ${comp[f] ? FIELD_COLORS[f].on : FIELD_COLORS[f].off}`}
                        title={`${COMP_LABELS[ck]} - ${f}: ${comp[f] ? 'Done' : 'Not done'}${comp[f + '_date'] || comp[f === 'done' ? 'done_date' : f === 'planned' ? 'plan_date' : 'ship_date'] ? ' (' + (comp[f === 'done' ? 'done_date' : f === 'planned' ? 'plan_date' : 'ship_date']) + ')' : ''}`}
                        data-testid={`tracker-${s.set_number}-${ck}-${f}`}
                      >
                        {comp[f] ? <Check className="w-3 h-3 mx-auto" /> : FIELD_LABELS[f]}
                      </button>
                      )}
                    </td>
                  ));
                })}
                <td className="px-2 py-1.5 text-center">
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${
                    s.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                    s.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                    s.status === 'in_progress' ? 'bg-blue-100 text-blue-600' :
                    'bg-slate-100 text-slate-400'
                  }`} data-testid={`tracker-status-${s.set_number}`}>
                    {s.overall_pct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 flex items-center gap-4 text-[9px] text-slate-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> P = Planned</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> D = Done</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-500" /> S = Shipped</span>
      </div>
      </>
      )}
    </div>
  );
}

// ============= MDP SUMMARY PANEL (for Train projects) =============
function MdpSummaryPanel({ projectId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/mdp/project-summary/${projectId}?year=${currentYear}`);
        setSummary(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [projectId, currentYear]);

  if (loading) return <div className="p-2 text-xs text-slate-400">Loading MDP...</div>;
  if (!summary || !summary.has_mdp_data) return null;

  return (
    <div className="mt-2.5 space-y-2" data-testid={`mdp-summary-${projectId}`}>
      {/* Seat breakdown by type (2P, 1P) */}
      <div className="space-y-1">
        {(summary.seat_breakdown || []).map(sb => (
          <div key={sb.label} className="flex items-center gap-2 text-[11px]">
            <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold min-w-[24px] text-center">{sb.label}</span>
            <span className="text-blue-700 font-bold">{sb.types}</span>
            <span className="text-blue-500">types</span>
            <span className="text-slate-300">/</span>
            <span className="text-blue-700 font-bold">{sb.seats?.toLocaleString()}</span>
            <span className="text-blue-500">seats</span>
            <span className="text-slate-300">/</span>
            <span className="text-blue-700 font-bold">{sb.sittings?.toLocaleString()}</span>
            <span className="text-blue-500">seat placed</span>
          </div>
        ))}
        <div className="flex items-center gap-2 text-[11px] pt-0.5 border-t border-slate-100">
          <Armchair className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-slate-700 font-bold">{summary.total_seat_mdp?.toLocaleString()}</span>
          <span className="text-slate-500">total seats</span>
          <span className="text-slate-300">/</span>
          <span className="text-blue-700 font-bold">{summary.total_seat_sittings?.toLocaleString()}</span>
          <span className="text-blue-500">total seat placed</span>
          <span className="text-slate-300 ml-2">/</span>
          <Package className="w-3 h-3 text-slate-400 ml-1" />
          <span className="text-slate-600 font-bold">{summary.other_types}</span>
          <span className="text-slate-500">other comp.</span>
        </div>
      </div>
    </div>
  );
}

// ============= PROJECT CARD =============
function ProjectCard({ project: p, onEdit, onDelete, onOrderUpdate, onShippedUpdate, onOpenMdp, onRefreshProjects, isAdmin = true }) {
  const cfg = CATEGORY_CONFIG[p.category] || CATEGORY_CONFIG['Train'];
  const Icon = cfg.icon;
  const isSet = p.planning_unit === 'set';
  const isTrain = p.planning_unit === 'seat';
  const isAlstomNJT = isTrain && (p.name || '').toLowerCase().includes('alstom');
  const isSeatPlaced = isTrain && !isAlstomNJT;
  const seatsPerSet = p.seats_per_set || 0;
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderQty, setOrderQty] = useState(p.ordered_quantity || 0);
  const [editingShipped, setEditingShipped] = useState(false);
  const [shippedQty, setShippedQty] = useState(p.total_shipped || 0);

  const ordered = p.ordered_quantity || 0;
  const planned = p.total_planned || 0;
  const completed = p.total_completed || 0;
  const shipped = p.total_shipped || 0;
  const notShipped = Math.max(0, completed - shipped);
  const shortage = p.shortage || 0;
  const pct = ordered > 0 ? Math.round((completed / ordered) * 100) : (planned > 0 ? Math.round((completed / planned) * 100) : 0);

  const handleSaveOrder = async () => {
    await onOrderUpdate(p.id, parseInt(orderQty) || 0);
    setEditingOrder(false);
  };

  const handleSaveShipped = async () => {
    await onShippedUpdate(p.id, parseInt(shippedQty) || 0);
    setEditingShipped(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`project-card-${p.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color} border`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-slate-800">{p.name}</h3>
              {p.sr_number && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{p.sr_number}</span>}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${cfg.color}`}>{p.category}</span>
              {p.seat_type_name && (
                <span className="text-[10px] font-semibold text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full">{p.seat_type_name}</span>
              )}
              {p.category !== 'City Bus' && p.has_nemo && p.nemo_seat_type && (
                <span className="text-[10px] font-semibold text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full">{p.nemo_seat_type}</span>
              )}
              {p.category !== 'City Bus' && p.lower_deck_code && (
                <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">LD: {p.lower_deck_code}</span>
              )}
              {p.category !== 'City Bus' && p.upper_deck_code && (
                <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">UD: {p.upper_deck_code}</span>
              )}
              {p.category !== 'City Bus' && p.center_deck_code && (
                <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">CD: {p.center_deck_code}</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500 flex-wrap">
              {p.customer_name && <span>Customer: <strong>{p.customer_name}</strong></span>}
              {p.line_code && <span>Line: <strong>{p.line_code}</strong></span>}
              {p.bus_feet && <span className="text-green-600 font-medium">{p.bus_feet}ft</span>}
              {p.project_code && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200 font-mono font-semibold">{p.project_code}</span>}
              {isSet && (
                <span className="flex items-center gap-1">
                  {p.seat_type_name && <span className="font-medium text-violet-600">{p.seat_type_name}: {p.units_per_set}</span>}
                  {p.category !== 'City Bus' && p.has_nemo && p.nemo_seat_type && (
                    <>
                      {p.seat_type_name && <span className="text-slate-300">+</span>}
                      <span className="font-medium text-teal-600">{p.nemo_seat_type}: {p.nemo_seats_per_set}</span>
                    </>
                  )}
                  {(p.seat_type_name || (p.category !== 'City Bus' && p.has_nemo && p.nemo_seat_type)) && (
                    <span className="text-slate-400">= <strong className="text-slate-600">{(p.units_per_set || 0) + (p.category !== 'City Bus' && p.has_nemo ? (p.nemo_seats_per_set || 0) : 0)}</strong> seats/set</span>
                  )}
                  {!p.seat_type_name && !(p.category !== 'City Bus' && p.has_nemo && p.nemo_seat_type) && <span>{p.units_per_set} seats/set</span>}
                </span>
              )}
              <span className="font-medium">{isSeatPlaced ? 'Per Seat Placed' : isTrain ? 'Per Seat' : 'Per Set'}</span>
              {isSeatPlaced && seatsPerSet > 0 && <span className="text-violet-600 font-semibold">{seatsPerSet} sp/set</span>}
            </div>

            {/* City Bus: Deck Codes & NFI Info Row */}
            {p.category === 'City Bus' && (
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {p.location && <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${p.location === 'KSU' ? 'text-indigo-700 bg-indigo-50 border border-indigo-200' : 'text-orange-700 bg-orange-50 border border-orange-200'}`}>{p.location}</span>}
                {p.bus_feet && <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">{p.bus_feet}ft Bus</span>}
                {p.project_code && <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded font-mono">{p.project_code}</span>}
                {p.lower_deck_code && <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">Lower Deck: <span className="font-mono">{p.lower_deck_code}</span></span>}
                {p.upper_deck_code && <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded">Upper Deck: <span className="font-mono">{p.upper_deck_code}</span></span>}
                {p.center_deck_code && <span className="text-[10px] font-semibold text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">Center Deck: <span className="font-mono">{p.center_deck_code}</span></span>}
                {!p.bus_feet && !p.project_code && !p.lower_deck_code && !p.upper_deck_code && (
                  <span className="text-[10px] text-slate-400 italic">No deck/NFI codes set — edit project to add</span>
                )}
              </div>
            )}
            {/* City Bus: Variant CRR NFI badges (label + code) — reflects extra NFIs uploaded via CRR panel */}
            {p.category === 'City Bus' && <CrrVariantsBadges projectId={p.id} />}
            {/* Sales Orders row removed — SO info is now shown per-set inside the Set Schedule table */}

            {/* Train: MDP Summary (only Alstom NJT) */}
            {isAlstomNJT && <MdpSummaryPanel projectId={p.id} />}

            {/* Seat Placed Train projects: Stats Row */}
            {isSeatPlaced && (
              <>
                <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Ordered:</span>
                    {isAdmin && editingOrder ? (
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" value={orderQty} onChange={e => setOrderQty(e.target.value)}
                          onBlur={handleSaveOrder}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveOrder(); if (e.key === 'Escape') { setOrderQty(ordered); setEditingOrder(false); } }}
                          className="w-16 border border-violet-400 rounded px-1.5 py-0.5 text-xs font-bold text-violet-700 text-center focus:outline-none focus:ring-1 focus:ring-violet-400"
                          autoFocus />
                        <span className="text-[10px] text-slate-400">sets</span>
                      </div>
                    ) : (
                      <span className={`text-xs font-bold text-violet-700 ${isAdmin ? 'cursor-pointer hover:bg-violet-50' : ''} px-1.5 py-0.5 rounded`}
                        onClick={() => isAdmin && setEditingOrder(true)} title={isAdmin ? "Click to edit" : ''}>
                        {ordered} sets
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Produced:</span>
                    <span className="text-xs font-bold text-emerald-600">{completed} <span className="text-[9px] font-normal text-slate-400">seat placed</span></span>
                  </div>
                  {seatsPerSet > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 uppercase font-semibold">Sets Done:</span>
                      <span className="text-xs font-bold text-blue-600">{p.sets_completed || Math.floor(completed / seatsPerSet)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Shipped:</span>
                    {isAdmin && editingShipped ? (
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" value={shippedQty} onChange={e => setShippedQty(e.target.value)}
                          onBlur={handleSaveShipped}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveShipped(); if (e.key === 'Escape') { setShippedQty(shipped); setEditingShipped(false); } }}
                          className="w-16 border border-violet-400 rounded px-1.5 py-0.5 text-xs font-bold text-violet-700 text-center focus:outline-none focus:ring-1 focus:ring-violet-400"
                          autoFocus />
                        <span className="text-[10px] text-slate-400">sp</span>
                      </div>
                    ) : (
                      <span className={`text-xs font-bold text-violet-600 ${isAdmin ? 'cursor-pointer hover:bg-violet-50' : ''} px-1.5 py-0.5 rounded`}
                        onClick={() => isAdmin && setEditingShipped(true)} title={isAdmin ? "Click to edit" : ''}>
                        {shipped} <span className="text-[9px] font-normal text-slate-400">sp</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                {seatsPerSet > 0 && ordered > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                      <span>{Math.floor(completed / seatsPerSet)}/{ordered} sets</span>
                      <span className="font-semibold">{Math.min(100, Math.round((completed / (ordered * seatsPerSet)) * 100))}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${completed >= ordered * seatsPerSet ? 'bg-emerald-500' : completed > 0 ? 'bg-amber-500' : 'bg-slate-200'}`}
                        style={{ width: `${Math.min(100, Math.round((completed / (ordered * seatsPerSet)) * 100))}%` }} />
                    </div>
                  </div>
                )}

                {/* Plan Entries Detail */}
                <PlanEntriesDetail projectId={p.id} isAdmin={isAdmin} />
              </>
            )}

            {/* Set-based: Stats Row */}
            {isSet && (
              <>
                <div className="flex items-center gap-4 mt-2.5 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Ordered:</span>
                    {isAdmin && editingOrder ? (
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" value={orderQty} onChange={e => setOrderQty(e.target.value)}
                          onBlur={handleSaveOrder}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveOrder(); if (e.key === 'Escape') { setOrderQty(ordered); setEditingOrder(false); } }}
                          className="w-16 border border-violet-400 rounded px-1.5 py-0.5 text-xs font-bold text-violet-700 text-center focus:outline-none focus:ring-1 focus:ring-violet-400"
                          autoFocus data-testid={`edit-order-${p.id}`} />
                        <span className="text-[10px] text-slate-400">sets</span>
                      </div>
                    ) : (
                      <span className={`text-xs font-bold text-violet-700 ${isAdmin ? 'cursor-pointer hover:bg-violet-50' : ''} px-1.5 py-0.5 rounded`}
                        onClick={() => isAdmin && setEditingOrder(true)} title={isAdmin ? "Click to edit" : ''}
                        data-testid={`order-qty-${p.id}`}>
                        {ordered} sets
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Planned:</span>
                    <span className="text-xs font-bold text-blue-600">{planned}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Completed:</span>
                    <span className="text-xs font-bold text-emerald-600">{completed}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Shipped:</span>
                    {isAdmin && editingShipped ? (
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" value={shippedQty} onChange={e => setShippedQty(e.target.value)}
                          onBlur={handleSaveShipped}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveShipped(); if (e.key === 'Escape') { setShippedQty(shipped); setEditingShipped(false); } }}
                          className="w-16 border border-violet-400 rounded px-1.5 py-0.5 text-xs font-bold text-violet-700 text-center focus:outline-none focus:ring-1 focus:ring-violet-400"
                          autoFocus data-testid={`edit-shipped-${p.id}`} />
                      </div>
                    ) : (
                      <span className={`text-xs font-bold text-violet-600 ${isAdmin ? 'cursor-pointer hover:bg-violet-50' : ''} px-1.5 py-0.5 rounded`}
                        onClick={() => isAdmin && setEditingShipped(true)} title={isAdmin ? "Click to edit" : ''}
                        data-testid={`shipped-qty-${p.id}`}>
                        {shipped}
                      </span>
                    )}
                  </div>
                  {notShipped > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 uppercase font-semibold">Not Shipped:</span>
                      <span className="text-xs font-bold text-amber-600">{notShipped}</span>
                    </div>
                  )}
                  {ordered > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 uppercase font-semibold">Backlog:</span>
                      <span className={`text-xs font-bold ${shortage > 0 ? 'text-red-500' : 'text-slate-300'}`}>{shortage}</span>
                    </div>
                  )}
                </div>

                {/* Progress bar */}
                {(ordered > 0 || planned > 0) && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                      <span>{ordered > 0 ? `${completed}/${ordered} ordered` : `${completed}/${planned} planned`}</span>
                      <span className="font-semibold">{pct}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-500' : 'bg-slate-200'}`}
                        style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                )}

                {/* Plan Entries Detail (for debugging totals) */}
                {isSet && <PlanEntriesDetail projectId={p.id} isAdmin={isAdmin} />}

                {/* Set Composition display */}
                {(p.set_components || []).length > 0 && (
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] text-slate-400 uppercase font-semibold">Set Composition:</span>
                    {p.set_components.map(sc => (
                      <span key={sc.name} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                        {sc.name} <span className="text-slate-400">({sc.weight}%)</span>
                        {sc.qty_per_set > 0 && <span className="text-indigo-500 ml-1">x{sc.qty_per_set}/set</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* City Bus: Set Schedule (Dock Dates) + Read-only Component Tracker */}
                {p.category === 'City Bus' ? (
                  <>
                    {/* Unscheduled sets warning */}
                    {(() => {
                      const schedules = p.set_schedules || [];
                      const withDate = schedules.filter(s => s.line_entry_date).length;
                      const missing = ordered - withDate;
                      return missing > 0 ? (
                        <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <span className="text-[10px] text-amber-700">
                            <span className="font-bold">{missing}</span> of {ordered} sets have no dock date
                            {schedules.length < ordered && <span className="text-amber-500"> ({ordered - schedules.length} sets not created yet)</span>}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    <SetSchedulePanel projectId={p.id} orderedQty={ordered} seatTypeName={p.seat_type_name} isAdmin={isAdmin} salesOrders={p.sales_orders} />
                    <CityBusSetTracker projectId={p.id} orderedQty={ordered} readOnly />
                    <CRRPanel projectId={p.id} isAdmin={isAdmin} />
                  </>
                ) : (
                  <>
                    <SetSchedulePanel projectId={p.id} orderedQty={ordered} seatTypeName={p.seat_type_name} salesOrders={p.sales_orders} />
                    {p.category === 'Coach' && <CRRPanel projectId={p.id} isAdmin={isAdmin} />}
                  </>
                )}
              </>
            )}

            {/* Train: MDP Cockpit button (only Alstom NJT) */}
            {isAlstomNJT && (
              <div className="mt-2">
                <button onClick={() => onOpenMdp(p.id, p.name)}
                  className="flex items-center gap-1.5 text-[11px] text-amber-600 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-2.5 py-1.5 rounded-md transition-colors border border-amber-200"
                  data-testid={`open-mdp-${p.id}`}>
                  <BarChart3 className="w-3.5 h-3.5" />
                  MDP Planning Cockpit
                </button>
              </div>
            )}

            {/* Hitachi Config Panel */}
            {p.hitachi_seat_types?.length > 0 && (
              <HitachiConfigPanel projectId={p.id} isAdmin={isAdmin} />
            )}
          </div>
        </div>
        {isAdmin && (
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <button onClick={() => onEdit(p)} className="p-2 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-project-${p.id}`}>
            <Edit3 className="w-4 h-4 text-blue-500" />
          </button>
          <button onClick={() => onDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-project-${p.id}`}>
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// ============= MAIN PAGE =============
export default function PlanningProjects({ isAdmin = true }) {
  const [projects, setProjects] = useState([]);
  const [lines, setLines] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showMdpImport, setShowMdpImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', customer_name: '', category: 'Train', sr_number: '',
    seat_type_name: '', line_id: '', units_per_set: 1, ordered_quantity: 0,
    set_components: [], has_nemo: false, nemo_seat_type: '', nemo_seats_per_set: 0,
    bus_feet: '', project_code: '', lower_deck_code: '', upper_deck_code: '', center_deck_code: '', has_center_deck: false,
    notes: '', seats_per_set: 0, sales_orders: [], location: ''
  });
  const [filterCat, setFilterCat] = useState('all');
  const [hideCompleted, setHideCompleted] = useState(true);
  const [mdpProject, setMdpProject] = useState(null); // { id, name }
  const [shipDaysOffset, setShipDaysOffset] = useState(5);
  const [editingOffset, setEditingOffset] = useState(false);
  const [tempOffset, setTempOffset] = useState(5);
  const [veryLateDays, setVeryLateDays] = useState(7);
  const [editingVeryLate, setEditingVeryLate] = useState(false);
  const [tempVeryLate, setTempVeryLate] = useState(7);

  const isProjectCompleted = (p) => {
    if (p.planning_unit === 'set') {
      return (p.ordered_quantity || 0) > 0 && (p.total_shipped || 0) >= (p.ordered_quantity || 0);
    }
    return false;
  };

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/planning/projects`);
      setProjects(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchLines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/lines`);
      setLines(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchProjects(); fetchLines(); }, [fetchProjects, fetchLines]);

  // Fetch ship days offset from settings
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/settings`);
        if (res.data?.ship_days_offset !== undefined) {
          setShipDaysOffset(res.data.ship_days_offset);
          setTempOffset(res.data.ship_days_offset);
        }
        if (res.data?.very_late_days !== undefined) {
          setVeryLateDays(res.data.very_late_days);
          setTempVeryLate(res.data.very_late_days);
        }
      } catch { }
    })();
  }, []);

  const saveShipOffset = async () => {
    try {
      await axios.put(`${API}/api/settings`, { ship_days_offset: parseInt(tempOffset) || 5 });
      setShipDaysOffset(parseInt(tempOffset) || 5);
      setEditingOffset(false);
      toast.success(`Ship days offset updated to ${tempOffset}`);
    } catch { toast.error('Failed to update'); }
  };

  const saveVeryLate = async () => {
    try {
      await axios.put(`${API}/api/settings`, { very_late_days: parseInt(tempVeryLate) || 7 });
      setVeryLateDays(parseInt(tempVeryLate) || 7);
      setEditingVeryLate(false);
      toast.success(`Very late threshold updated to ${tempVeryLate} days`);
    } catch { toast.error('Failed to update'); }
  };

  const resetForm = () => {
    setForm({ name: '', customer_name: '', category: 'Train', sr_number: '', seat_type_name: '', line_id: '', units_per_set: 1, ordered_quantity: 0, set_components: [], has_nemo: false, nemo_seat_type: '', nemo_seats_per_set: 0, bus_feet: '', project_code: '', lower_deck_code: '', upper_deck_code: '', center_deck_code: '', has_center_deck: false, notes: '', seats_per_set: 0, sales_orders: [], location: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name || '', customer_name: p.customer_name || '', category: p.category || 'Train',
      sr_number: p.sr_number || '', seat_type_name: p.seat_type_name || '', line_id: p.line_id || '',
      units_per_set: p.units_per_set || 1, ordered_quantity: p.ordered_quantity || 0,
      set_components: p.set_components || [],
      has_nemo: p.has_nemo || false, nemo_seat_type: p.nemo_seat_type || '', nemo_seats_per_set: p.nemo_seats_per_set || 0,
      bus_feet: p.bus_feet || '', project_code: p.project_code || '',
      lower_deck_code: p.lower_deck_code || '', upper_deck_code: p.upper_deck_code || '',
      center_deck_code: p.center_deck_code || '', has_center_deck: p.has_center_deck || false,
      notes: p.notes || '', seats_per_set: p.seats_per_set || 0,
      sales_orders: p.sales_orders || [], location: p.location || ''
    });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Project name is required'); return; }
    // Clean up set_numbers_text from sales_orders before saving
    const cleanForm = { ...form };
    if (cleanForm.sales_orders) {
      cleanForm.sales_orders = cleanForm.sales_orders.map(so => ({
        so_number: so.so_number,
        set_numbers: so.set_numbers || [],
      }));
    }
    try {
      if (editing) {
        await axios.put(`${API}/api/planning/projects/${editing}`, cleanForm);
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/api/planning/projects`, cleanForm);
        toast.success('Project created');
      }
      resetForm();
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? Plans will not be deleted.')) return;
    try {
      await axios.delete(`${API}/api/planning/projects/${id}`);
      toast.success('Deleted');
      fetchProjects();
    } catch (err) { toast.error('Error'); }
  };

  const handleOrderUpdate = async (projectId, qty) => {
    try {
      await axios.put(`${API}/api/planning/projects/${projectId}`, { ordered_quantity: qty });
      fetchProjects();
    } catch (err) { toast.error('Error updating order'); }
  };

  const handleShippedUpdate = async (projectId, qty) => {
    try {
      await axios.put(`${API}/api/planning/projects/${projectId}/shipped`, { total_shipped: qty });
      toast.success('Shipped quantity updated');
      fetchProjects();
    } catch (err) { toast.error('Error updating shipped'); }
  };

  const unitLabel = form.category === 'Train' ? 'per seat' : 'per set';
  const isSetCategory = form.category !== 'Train';

  return (
    <div className="space-y-5" data-testid="planning-projects-page">
      {/* When MDP Cockpit is open, show it instead of projects list */}
      {mdpProject ? (
        <MdpCockpit
          projectId={mdpProject.id}
          projectName={mdpProject.name}
          onBack={() => setMdpProject(null)}
        />
      ) : (
      <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Planning Projects</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage projects & order quantities for production planning</p>
        </div>
        {isAdmin && (
        <div className="flex items-center gap-2">
          <button onClick={() => setShowMdpImport(true)}
            className="px-4 py-2 text-xs font-medium bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 flex items-center gap-1.5"
            data-testid="mdp-import-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> MDP / Dock Date Import
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="px-4 py-2 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5"
            data-testid="add-project-btn">
            <Plus className="w-3.5 h-3.5" /> New Project
          </button>
        </div>
        )}
      </div>

      {/* Project Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="project-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-800">{editing ? 'Edit Project' : 'New Project'}</h3>
            <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Category *</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="form-category">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Planning: {unitLabel}</span>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Project Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="e.g. GRTC" data-testid="form-name" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Customer</label>
              <input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="e.g. NewFlyer" data-testid="form-customer" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Program Number</label>
              <input value={form.sr_number} onChange={e => setForm(f => ({ ...f, sr_number: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="e.g. PGM-3155" data-testid="form-sr" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">{isSetCategory ? 'Main Seat Type' : 'Seat Type'}</label>
              <input value={form.seat_type_name} onChange={e => setForm(f => ({ ...f, seat_type_name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="e.g. Avance 2050" data-testid="form-seat-type" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Production Line</label>
              <select value={form.line_id} onChange={e => setForm(f => ({ ...f, line_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="form-line">
                <option value="">-- None --</option>
                {lines.map(l => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
              </select>
            </div>
            {isSetCategory && (
              <>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">{form.seat_type_name ? `${form.seat_type_name} Seats/Set` : 'Seats per Set'}</label>
                  <input type="number" min="0" value={form.units_per_set} onChange={e => setForm(f => ({ ...f, units_per_set: parseInt(e.target.value) || 0 }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="form-units" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Ordered Quantity (sets)</label>
                  <input type="number" min="0" value={form.ordered_quantity} onChange={e => setForm(f => ({ ...f, ordered_quantity: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-violet-300 rounded-lg px-3 py-2 text-sm mt-1 text-violet-700 font-semibold" placeholder="Total sets ordered" data-testid="form-ordered" />
                </div>
              </>
            )}
            {/* Train Seat Placed mode fields (non-Alstom NJT) */}
            {!isSetCategory && (
              <>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Seat Placed / Set</label>
                  <input type="number" min="0" value={form.seats_per_set} onChange={e => setForm(f => ({ ...f, seats_per_set: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm mt-1 text-blue-700 font-semibold" placeholder="e.g. 200" data-testid="form-seats-per-set" />
                  <span className="text-[9px] text-slate-400 mt-0.5 block">How many seat placed = 1 set complete</span>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Ordered (sets)</label>
                  <input type="number" min="0" value={form.ordered_quantity} onChange={e => setForm(f => ({ ...f, ordered_quantity: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-violet-300 rounded-lg px-3 py-2 text-sm mt-1 text-violet-700 font-semibold" placeholder="Total sets ordered" data-testid="form-ordered-train" />
                </div>
              </>
            )}
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="Optional notes" data-testid="form-notes" />
            </div>
          </div>

          {/* Nemo Seat Section for Set-based projects */}
          {isSetCategory && form.category !== 'City Bus' && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2" data-testid="nemo-checkbox-label">
                <input type="checkbox" checked={form.has_nemo}
                  onChange={e => setForm(f => ({ ...f, has_nemo: e.target.checked, ...(!e.target.checked && { nemo_seat_type: '', nemo_seats_per_set: 0 }) }))}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid="nemo-checkbox" />
                <span className="text-[11px] font-semibold text-slate-700">Nemo seat included in set?</span>
                <span className="text-[10px] text-slate-400">(High Back Nemo / HB Nemo)</span>
              </label>
              {form.has_nemo && (
                <div className="grid grid-cols-2 gap-3 ml-6 p-3 bg-teal-50/50 border border-teal-200 rounded-lg">
                  <div>
                    <label className="text-[10px] font-semibold text-teal-700 uppercase">Nemo Seat Type</label>
                    <input value={form.nemo_seat_type} onChange={e => setForm(f => ({ ...f, nemo_seat_type: e.target.value }))}
                      className="w-full border border-teal-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-teal-400 focus:border-teal-400" placeholder="e.g. High Back Nemo" data-testid="form-nemo-type" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-teal-700 uppercase">Nemo Seats per Set</label>
                    <input type="number" min="1" value={form.nemo_seats_per_set} onChange={e => setForm(f => ({ ...f, nemo_seats_per_set: parseInt(e.target.value) || 0 }))}
                      className="w-full border border-teal-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-teal-400 focus:border-teal-400" data-testid="form-nemo-count" />
                  </div>
                  <div className="col-span-2 text-[10px] text-teal-600 font-medium">
                    Total seats per set: <strong>{(form.units_per_set || 0) + (form.nemo_seats_per_set || 0)}</strong>
                    {form.seat_type_name && <span> ({form.seat_type_name}: {form.units_per_set || 0}</span>}
                    {form.seat_type_name && form.nemo_seat_type && <span> + {form.nemo_seat_type}: {form.nemo_seats_per_set || 0})</span>}
                    {form.seat_type_name && !form.nemo_seat_type && <span> + Nemo: {form.nemo_seats_per_set || 0})</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* City Bus specific fields */}
          {form.category === 'City Bus' && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <label className="text-[11px] font-bold text-green-700 mb-2 block">City Bus Details</label>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Location</label>
                  <select value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400" data-testid="form-location">
                    <option value="">-- Select --</option>
                    <option value="KSU">KSU</option>
                    <option value="Kiel">Kiel</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Bus Feet</label>
                  <input value={form.bus_feet || ''} onChange={e => setForm(f => ({ ...f, bus_feet: e.target.value }))}
                    className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400" placeholder="e.g. 40" data-testid="form-bus-feet" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Project Code</label>
                  <input value={form.project_code || ''} onChange={e => setForm(f => ({ ...f, project_code: e.target.value }))}
                    className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400 font-mono" placeholder="NFI00000xx" data-testid="form-project-code" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Lower Deck Code</label>
                  <input value={form.lower_deck_code || ''} onChange={e => setForm(f => ({ ...f, lower_deck_code: e.target.value }))}
                    className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400 font-mono" placeholder="LD-001" data-testid="form-lower-deck" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Upper Deck Code</label>
                  <input value={form.upper_deck_code || ''} onChange={e => setForm(f => ({ ...f, upper_deck_code: e.target.value }))}
                    className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400 font-mono" placeholder="UD-001" data-testid="form-upper-deck" />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer mt-5">
                    <input type="checkbox" checked={form.has_center_deck || false}
                      onChange={e => setForm(f => ({ ...f, has_center_deck: e.target.checked, ...(!e.target.checked && { center_deck_code: '' }) }))}
                      className="w-4 h-4 rounded border-green-300 text-green-600 focus:ring-green-500" data-testid="form-center-deck-check" />
                    <span className="text-[11px] font-semibold text-slate-700">Center Deck included?</span>
                  </label>
                  {form.has_center_deck && (
                    <input value={form.center_deck_code || ''} onChange={e => setForm(f => ({ ...f, center_deck_code: e.target.value }))}
                      className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm mt-1 focus:ring-green-400 font-mono" placeholder="CD-001" data-testid="form-center-deck-code" />
                  )}
                </div>
              </div>
              {/* Sales Orders */}
              <div className="mt-3 border-t border-green-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Sales Orders</label>
                  <button type="button" onClick={() => setForm(f => ({ ...f, sales_orders: [...(f.sales_orders || []), { so_number: '', set_numbers: [] }] }))}
                    className="text-[10px] font-semibold text-green-600 hover:text-green-700" data-testid="add-so-btn">+ Add SO</button>
                </div>
                {(form.sales_orders || []).map((so, si) => (
                  <div key={si} className="flex items-center gap-2 mb-1.5">
                    <input value={so.so_number} onChange={e => {
                      const updated = [...form.sales_orders];
                      updated[si] = { ...updated[si], so_number: e.target.value };
                      setForm(f => ({ ...f, sales_orders: updated }));
                    }}
                      className="w-32 border border-green-200 rounded-lg px-2 py-1.5 text-sm font-mono focus:ring-green-400" placeholder="SO #" />
                    <input value={so.set_numbers_text !== undefined ? so.set_numbers_text : (so.set_numbers || []).join(', ')} onChange={e => {
                      const raw = e.target.value;
                      // Parse ranges (1-18) and individual numbers (1, 2, 5)
                      const nums = [];
                      raw.split(',').forEach(part => {
                        const trimmed = part.trim();
                        const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
                        if (rangeMatch) {
                          const start = parseInt(rangeMatch[1]);
                          const end = parseInt(rangeMatch[2]);
                          for (let i = Math.min(start, end); i <= Math.max(start, end); i++) nums.push(i);
                        } else {
                          const n = parseInt(trimmed);
                          if (!isNaN(n)) nums.push(n);
                        }
                      });
                      const updated = [...form.sales_orders];
                      updated[si] = { ...updated[si], set_numbers_text: raw, set_numbers: [...new Set(nums)].sort((a,b) => a-b) };
                      setForm(f => ({ ...f, sales_orders: updated }));
                    }}
                      className="flex-1 border border-green-200 rounded-lg px-2 py-1.5 text-sm focus:ring-green-400" placeholder="Set numbers (e.g. 1, 2, 3) — leave empty for project-level" />
                    <button onClick={() => setForm(f => ({ ...f, sales_orders: f.sales_orders.filter((_, i) => i !== si) }))}
                      className="p-1 text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {(!form.sales_orders || form.sales_orders.length === 0) && (
                  <p className="text-[10px] text-slate-400 italic">No sales orders added yet</p>
                )}
              </div>
            </div>
          )}

          {/* Set Components (Composition) */}
          {isSetCategory && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-semibold text-slate-500 uppercase">Set Composition (weights must total 100%)</label>
                <button type="button" onClick={() => setForm(f => ({ ...f, set_components: [...f.set_components, { name: '', weight: 0 }] }))}
                  className="text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="add-comp-btn">+ Add Component</button>
              </div>
              {form.set_components.length === 0 && (
                <p className="text-[10px] text-slate-400 italic">No sub-components defined. Add components to track weighted completion.</p>
              )}
              <div className="space-y-1.5">
                {form.set_components.map((sc, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={sc.name} onChange={e => { const arr = [...form.set_components]; arr[i] = { ...arr[i], name: e.target.value }; setForm(f => ({ ...f, set_components: arr })); }}
                      className="flex-1 border rounded-lg px-3 py-1.5 text-sm" placeholder="e.g. Std Seat" data-testid={`comp-name-${i}`} />
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" max="100" value={sc.weight} onChange={e => { const arr = [...form.set_components]; arr[i] = { ...arr[i], weight: parseInt(e.target.value) || 0 }; setForm(f => ({ ...f, set_components: arr })); }}
                        className="w-16 border rounded-lg px-2 py-1.5 text-sm text-center font-bold" data-testid={`comp-weight-${i}`} />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                    <button type="button" onClick={() => setForm(f => ({ ...f, set_components: f.set_components.filter((_, j) => j !== i) }))}
                      className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                ))}
              </div>
              {form.set_components.length > 0 && (
                <div className="mt-1.5 text-[10px]">
                  <span className={`font-bold ${form.set_components.reduce((s, c) => s + c.weight, 0) === 100 ? 'text-emerald-600' : 'text-red-500'}`}>
                    Total: {form.set_components.reduce((s, c) => s + c.weight, 0)}%
                  </span>
                  {form.set_components.reduce((s, c) => s + c.weight, 0) !== 100 && <span className="text-red-400 ml-1">(must be 100%)</span>}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={resetForm} className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5" data-testid="form-save-btn">
              <Save className="w-3.5 h-3.5" /> {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Category Filter + Ship Offset Setting + Project List */}
      <div className="space-y-3" data-testid="project-list">
        {/* Ship Days Offset Setting */}
        <div className="flex items-center gap-4 bg-white rounded-lg px-4 py-2.5 border border-slate-200 shadow-sm flex-wrap" data-testid="ship-offset-setting">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-600 font-semibold whitespace-nowrap">Ship Date = Plan Date -</span>
            {editingOffset ? (
              <>
                <input type="number" value={tempOffset} onChange={e => setTempOffset(e.target.value)}
                  className="w-14 border border-amber-300 rounded px-2 py-1 text-sm text-center font-bold focus:outline-none focus:ring-1 focus:ring-amber-400" min={0} max={30}
                  data-testid="ship-offset-input" />
                <span className="text-xs text-slate-500">days</span>
                <button onClick={saveShipOffset} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700" data-testid="ship-offset-save">Save</button>
                <button onClick={() => { setEditingOffset(false); setTempOffset(shipDaysOffset); }} className="text-xs px-2 py-1 text-slate-400 hover:text-slate-600">Cancel</button>
              </>
            ) : (
              <>
                <span className="text-sm font-bold text-slate-800" data-testid="ship-offset-value">{shipDaysOffset}</span>
                <span className="text-xs text-slate-500">days</span>
                {isAdmin && (
                  <button onClick={() => setEditingOffset(true)} className="text-xs px-2.5 py-1 bg-slate-200 text-slate-600 rounded font-semibold hover:bg-slate-300" data-testid="ship-offset-edit">Edit</button>
                )}
              </>
            )}
          </div>
          <div className="w-px h-5 bg-slate-200" />
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-400" />
            <span className="text-xs text-slate-600 font-semibold whitespace-nowrap">Very Late &gt;</span>
            {editingVeryLate ? (
              <>
                <input type="number" value={tempVeryLate} onChange={e => setTempVeryLate(e.target.value)}
                  className="w-14 border border-red-300 rounded px-2 py-1 text-sm text-center font-bold focus:outline-none focus:ring-1 focus:ring-red-400" min={1} max={60}
                  data-testid="very-late-input" />
                <span className="text-xs text-slate-500">days</span>
                <button onClick={saveVeryLate} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700" data-testid="very-late-save">Save</button>
                <button onClick={() => { setEditingVeryLate(false); setTempVeryLate(veryLateDays); }} className="text-xs px-2 py-1 text-slate-400 hover:text-slate-600">Cancel</button>
              </>
            ) : (
              <>
                <span className="text-sm font-bold text-red-600" data-testid="very-late-value">{veryLateDays}</span>
                <span className="text-xs text-slate-500">days</span>
                {isAdmin && (
                  <button onClick={() => setEditingVeryLate(true)} className="text-xs px-2.5 py-1 bg-slate-200 text-slate-600 rounded font-semibold hover:bg-slate-300" data-testid="very-late-edit">Edit</button>
                )}
              </>
            )}
          </div>
        </div>
        {/* Category filter tabs */}
        <div className="flex items-center justify-between gap-3" data-testid="category-filter">
          <div className="flex items-center gap-1.5">
          {['all', 'Train', 'Coach', 'City Bus'].map(cat => {
            const catList = cat === 'all' ? projects : projects.filter(p => p.category === cat);
            const visibleCount = hideCompleted ? catList.filter(p => !isProjectCompleted(p)).length : catList.length;
            const active = filterCat === cat;
            const dotColor = cat === 'Train' ? 'bg-blue-500' : cat === 'Coach' ? 'bg-orange-500' : cat === 'City Bus' ? 'bg-green-500' : 'bg-slate-500';
            return (
              <button key={cat} onClick={() => setFilterCat(cat)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all flex items-center gap-1.5
                  ${active ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                data-testid={`filter-cat-${cat}`}>
                {cat !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : dotColor}`} />}
                {cat === 'all' ? 'All' : cat} ({visibleCount})
              </button>
            );
          })}
          </div>
          <button onClick={() => setHideCompleted(h => !h)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all border ${hideCompleted ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}
            data-testid="projects-hide-completed-toggle">
            {hideCompleted ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {hideCompleted ? 'Completed Hidden' : 'Show All'}
            {hideCompleted && projects.filter(p => isProjectCompleted(p)).length > 0 && (
              <span className="bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded text-[10px]">{projects.filter(p => isProjectCompleted(p)).length}</span>
            )}
          </button>
        </div>

        {projects.length === 0 && !showForm && (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <FolderKanban className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500 font-medium">No planning projects yet</p>
            {isAdmin && (
              <button onClick={() => setShowForm(true)} className="mt-3 px-4 py-2 text-xs bg-amber-600 text-white rounded-lg">
                <Plus className="w-3 h-3 inline mr-1" /> Add your first project
              </button>
            )}
          </div>
        )}

        {/* Grouped by category */}
        {['Train', 'Coach', 'City Bus'].map(cat => {
          const catProjects = projects.filter(p => p.category === cat && (filterCat === 'all' || filterCat === cat) && (!hideCompleted || !isProjectCompleted(p)));
          const hiddenCount = projects.filter(p => p.category === cat && (filterCat === 'all' || filterCat === cat) && isProjectCompleted(p)).length;
          if (catProjects.length === 0 && (!hideCompleted || hiddenCount === 0)) return null;
          const dotColor = cat === 'Train' ? 'bg-blue-500' : cat === 'Coach' ? 'bg-orange-500' : 'bg-green-500';
          const borderColor = cat === 'Train' ? 'border-blue-100' : cat === 'Coach' ? 'border-orange-100' : 'border-green-100';

          return (
            <div key={cat}>
              <div className={`flex items-center gap-2 mb-2 pb-1.5 border-b ${borderColor}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{cat}</span>
                <span className="text-[10px] text-slate-400">({catProjects.length} project{catProjects.length !== 1 ? 's' : ''})</span>
                {hideCompleted && hiddenCount > 0 && (
                  <span className="text-[10px] text-amber-500 italic">{hiddenCount} completed hidden</span>
                )}
              </div>
              {catProjects.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {catProjects.map(p => (
                    <ProjectCard key={p.id} project={p} onEdit={handleEdit} onDelete={handleDelete} onOrderUpdate={handleOrderUpdate} onShippedUpdate={handleShippedUpdate} onOpenMdp={(id, name) => setMdpProject({ id, name })} isAdmin={isAdmin} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-[11px] text-slate-400 italic mb-4">All projects completed</div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}

      <MdpImportModal open={showMdpImport} onClose={() => setShowMdpImport(false)} onComplete={fetchProjects} />
    </div>
  );
}
